import React, { useEffect, useRef, useState } from 'react';
import {
  ALWAYS_ON_NOTE,
  EMAIL_TYPES,
  fetchEmailAlerts,
  onEmailAlertsChanged,
  readCachedEmailAlerts,
  saveEmailAlerts,
} from '../services/emailAlertsApi';

/**
 * "Email alerts" — the first row of the ☰ menu (Email & Reports design, Oct 2026).
 *
 * One main ON/OFF switch, the four emails it governs, an arrow to their own switches, and a
 * fixed line saying what is always sent. Only owners see it — only owners receive these emails —
 * and whether someone is an owner is the server's answer, not this browser's.
 *
 * The switch moves the moment it is tapped; the choice is then saved on the server. If saving
 * fails the switch moves back and says "Could not save, try again". The last known position is
 * kept in this browser so it shows instantly next time; the server is asked on every open and
 * wins, so a change made on another device shows here too.
 */
function Toggle({ checked, onChange, label, disabled = false, small = false }) {
  const w = small ? 34 : 42;
  const h = small ? 20 : 24;
  const knob = h - 4;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(!checked); }}
      style={{
        position: 'relative',
        width: w,
        height: h,
        borderRadius: h,
        border: 'none',
        padding: 0,
        flexShrink: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: checked ? (disabled ? '#93c5fd' : '#2563eb') : '#cbd5e1',
        transition: 'background-color 0.15s ease',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? w - knob - 2 : 2,
          width: knob,
          height: knob,
          borderRadius: '50%',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          transition: 'left 0.15s ease',
        }}
      />
    </button>
  );
}

const EmailAlertsMenuRow = () => {
  const [prefs, setPrefs] = useState(() => readCachedEmailAlerts());
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    // The cached copy is already on screen; the server's answer corrects it.
    fetchEmailAlerts()
      .then((p) => { if (mounted.current) setPrefs(p); })
      .catch(() => { /* keep showing the copy; a save will report any real problem */ });
    const off = onEmailAlertsChanged((p) => { if (mounted.current) setPrefs((cur) => ({ ...p, isOwner: cur ? cur.isOwner || p.isOwner : p.isOwner })); });
    return () => { mounted.current = false; off(); };
  }, []);

  if (!prefs || !prefs.isOwner) return null;

  const apply = async (optimistic, change) => {
    const previous = prefs;
    setError('');
    setPrefs(optimistic);
    setSaving(true);
    try {
      const saved = await saveEmailAlerts(change);
      if (mounted.current) setPrefs((cur) => ({ ...saved, isOwner: cur.isOwner }));
    } catch (_) {
      if (mounted.current) {
        setPrefs(previous);
        setError('Could not save, try again');
      }
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const toggleMaster = (next) => apply({ ...prefs, master: next }, { master: next });
  const toggleType = (key, next) => apply({ ...prefs, types: { ...prefs.types, [key]: next } }, { types: { [key]: next } });

  return (
    <div
      data-testid="email-alerts-menu-row"
      style={{ padding: '12px 16px', borderBottom: '1px solid #eee', backgroundColor: '#ffffff' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Hide individual email switches' : 'Show individual email switches'}
          style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', minWidth: 0 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>
            <span aria-hidden="true">✉️</span>
            <span>Email alerts</span>
            <span aria-hidden="true" style={{ fontSize: '11px', color: '#6b7280', marginLeft: '2px' }}>{expanded ? '▲' : '▼'}</span>
          </div>
          <div style={{ fontSize: '11px', color: prefs.master ? '#6b7280' : '#9ca3af', marginTop: '2px' }}>
            {EMAIL_TYPES.map((t, i) => (
              <span key={t.key} style={{ textDecoration: prefs.master && prefs.types[t.key] ? 'none' : 'line-through' }}>
                {i > 0 ? ' · ' : ''}{t.label}
              </span>
            ))}
          </div>
        </button>
        <Toggle checked={prefs.master} onChange={toggleMaster} label="Email alerts" disabled={saving} />
      </div>

      {expanded && (
        <div style={{ marginTop: '10px', padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
          {EMAIL_TYPES.map((t) => (
            <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', color: prefs.master ? '#111827' : '#9ca3af' }}>{t.label}</div>
                <div style={{ fontSize: '10px', color: '#9ca3af' }}>{t.hint}</div>
              </div>
              <Toggle
                small
                checked={!!prefs.types[t.key]}
                onChange={(next) => toggleType(t.key, next)}
                label={`${t.label} email`}
                disabled={saving}
              />
            </div>
          ))}
          {!prefs.master && (
            <div style={{ fontSize: '10px', color: '#92400e', marginTop: '4px' }}>
              Email alerts are off. These choices are kept for when you turn them back on.
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '8px', lineHeight: 1.4 }}>{ALWAYS_ON_NOTE}</div>
      {error && (
        <div role="alert" style={{ fontSize: '11px', color: '#b91c1c', marginTop: '6px' }}>{error}</div>
      )}
    </div>
  );
};

export default EmailAlertsMenuRow;
